'use client';

import React from 'react';
import type { AdSlot } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';

function ensureAbsoluteUrl(url: string): string {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) return url;
  return `https://${url}`;
}

interface SidebarAdProps {
  slot?: AdSlot;
  /** When true, the parent is still fetching the ad slot from the
   *  server. Render a skeleton-sized placeholder so the layout doesn't
   *  jump when the slot resolves. */
  loading?: boolean;
}

export default function SidebarAd({ slot, loading }: SidebarAdProps) {
  const { isPro, loading: authLoading } = useAuth();
  // Plan #144.19 — don't render anything until we KNOW whether the
  // user is pro. Previously this returned the "Advertise Here"
  // placeholder on first mount (isPro=false during auth load), then
  // unmounted as soon as auth resolved true. That caused the ad to
  // briefly flash and disappear on every page refresh for pro
  // subscribers — the exact opposite of their opt-out.
  if (authLoading) return null;
  if (isPro) return null;
  // Plan #144.19 — while the parent is still fetching the ad slot,
  // show a dimmed placeholder of the correct size so the sidebar
  // column keeps its reserved width. Prevents a layout jump when
  // the slot payload arrives after auth.
  if (loading) {
    return (
      <div
        className="rounded-xl border border-default/50 flex-shrink-0 animate-pulse"
        style={{ width: '300px', minHeight: '600px', background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)' }}
        aria-hidden="true"
      />
    );
  }
  if (slot?.isActive && slot.imageUrl) {
    return (
      <div className="w-[300px] flex-shrink-0">
        <a href={ensureAbsoluteUrl(slot.targetUrl)} target="_blank" rel="noopener noreferrer">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={slot.imageUrl}
            alt={slot.altText}
            style={{ width: '300px', minHeight: '600px', objectFit: 'cover' }}
            className="rounded-xl"
          />
        </a>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-default flex flex-col items-center justify-center flex-shrink-0"
      style={{
        width: '300px',
        minHeight: '600px',
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
      }}
    >
      <div className="text-center px-4">
        <p className="text-[10px] text-slate-600 uppercase tracking-widest mb-2">Advertisement</p>
        <div className="w-16 h-16 rounded-xl bg-card-hover-bg flex items-center justify-center mx-auto mb-3">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </div>
        <p className="text-fg-muted text-base font-semibold mb-1">300×600</p>
        <p className="text-slate-600 text-xs mb-4">Premium sidebar placement</p>
        <a
          href="/advertise"
          className="inline-block text-xs text-sky-500 hover:text-sky-400 transition-colors border border-sky-500/30 hover:border-sky-400/50 px-3 py-1.5 rounded-lg"
        >
          Advertise Here
        </a>
      </div>
    </div>
  );
}
